import { useEffect, useState } from 'react'
import {
  ClipboardSignature,
  Plus,
  Trash2,
  Loader2,
  Check,
  Clock,
  AlertCircle,
  Info,
  Lock,
  RotateCw,
  ExternalLink,
} from 'lucide-react'
import { redirectToLogin } from '../lib/api.js'
import {
  Badge, Button, ChoiceChips, ConfirmDialog, DrawerFooter, EmptyState, ErrorState, Field, IconButton, Input, Modal, Textarea,
  fmtDateTime, useToast,
} from '../kit'

/**
 * Consent — CRM creates the record, patient portal handles acceptance.
 *   chart.consent[] = [{
 *     unique_id, type, custom_label?, note,
 *     expired_date, accept_at,
 *     created_at, created_by, modified_at, modified_by,
 *   }]
 *
 * - `type` is one of the 9 enum values; if `กำหนดเอง`, `custom_label`
 *   carries the user-provided name.
 * - `expired_date` is now+1h on insert (stamped by proxy). Past expiry shows
 *   as expired in the UI.
 * - `accept_at` is set by the patient portal only; CRM treats it as read-only.
 */

const CONSENT_TYPES = [
  { value: 'จัดฟัน',             tone: 'violet' },
  { value: 'รากฟันเทียม',         tone: 'indigo' },
  { value: 'ฟอกสีฟัน',           tone: 'amber'  },
  { value: 'ผ่าฟันคุด',           tone: 'rose'   },
  { value: 'ถอดเครื่องมือ',       tone: 'cyan'   },
  { value: 'ย้ายคลินิกจัดฟัน',    tone: 'blue'   },
  { value: 'รักษารากฟัน',         tone: 'orange' },
  // Hidden from the create picker (2026-10-16): no consent wording exists for it
  // yet in either app. Kept here so existing entries still render with their tone.
  { value: 'บัตรทอง extra',       tone: 'emerald', hidden: true },
  { value: 'กำหนดเอง',            tone: 'slate'  },
]

const TONE = {
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  ring: 'ring-violet-200',  dot: 'bg-violet-500' },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  ring: 'ring-indigo-200',  dot: 'bg-indigo-500' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200',   dot: 'bg-amber-500'  },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200',    dot: 'bg-rose-500'   },
  cyan:    { bg: 'bg-cyan-50',    text: 'text-cyan-700',    ring: 'ring-cyan-200',    dot: 'bg-cyan-500'   },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200',    dot: 'bg-blue-500'   },
  orange:  { bg: 'bg-orange-50',  text: 'text-orange-700',  ring: 'ring-orange-200',  dot: 'bg-orange-500' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', dot: 'bg-emerald-500'},
  slate:   { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200',   dot: 'bg-slate-500'  },
}

// Each tone's `dot` shade above as a CSS colour, for the kit's ChoiceChips.
const TONE_HEX = {
  violet: '#8b5cf6', indigo: '#6366f1', amber: '#f59e0b', rose: '#f43f5e', cyan: '#06b6d4',
  blue: '#3b82f6', orange: '#f97316', emerald: '#10b981', slate: '#64748b',
}

const CUSTOM_TYPE = 'กำหนดเอง'

export default function ConsentEditor({ chart, patientId, canEdit, isAdmin = false, onSaved }) {
  const consents  = Array.isArray(chart?.consent) ? chart.consent : []
  const documents = Array.isArray(chart?.document) ? chart.document : []
  const chartId   = chart?.id

  // The signed consent PDF lives in chart.document[] (template 'consent'),
  // NOT on the consent entry. sign.js stamps the document's created_at with the
  // same timestamp it writes to consent.accept_at, so that links the two.
  function matchedDocsFor(consent) {
    if (!consent?.accept_at) return []
    return documents
      .map((d, i) => ({ d, i }))
      .filter(({ d }) =>
        (d?.template === 'consent' || d?.doc_type === 'ใบยินยอม') &&
        d?.created_at === consent.accept_at,
      )
  }

  const [saving, setSaving] = useState(false)
  const [err, setErr]       = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  // While the delete runs the dialog stays open (busy) with the text it was confirmed
  // with — `consents` may refresh underneath it.
  const [deletingText, setDeletingText] = useState(null)
  const toast = useToast()

  // Force a re-render every minute so the expiry countdown updates.
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000)
    return () => clearInterval(t)
  }, [])

  async function patchConsent(next, doneMessage = 'บันทึกแล้ว') {
    setSaving(true); setErr(null)
    try {
      const r = await fetch(`/api/charts/${chartId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ consent: next }),
      })
      if (r.status === 401) { redirectToLogin(); return }
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${t || r.statusText}`)
      }
      toast.success(doneMessage)
      onSaved?.()
    } catch (e) {
      const msg = e?.message || 'บันทึกไม่สำเร็จ'
      setErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
    } finally {
      setSaving(false)
    }
  }

  function addConsent({ type, customLabel, note }) {
    const newEntry = {
      type,
      note: note || '',
    }
    if (type === CUSTOM_TYPE && customLabel) newEntry.custom_label = customLabel
    // unique_id, expired_date, created_*, modified_* stamped by proxy.
    patchConsent([...consents, newEntry], 'สร้างใบยินยอมแล้ว')
  }
  function updateNote(idx, note) {
    const next = consents.map((c, i) => i === idx ? { ...c, note } : c)
    patchConsent(next)
  }
  // Renew an expired (not-yet-accepted) consent: push expiry to now + 1 hour.
  // The proxy preserves an explicit expired_date (it only defaults when null).
  function renewConsent(idx) {
    const next = consents.map((c, i) =>
      i === idx ? { ...c, expired_date: Date.now() + 3600 * 1000 } : c,
    )
    patchConsent(next, 'ต่ออายุแล้ว')
  }
  // Delete a consent entry. For accepted consents this also removes the signed
  // consent PDF from chart.document[] AND deletes the underlying Wasabi object.
  function deleteConsentText(idx) {
    return matchedDocsFor(consents[idx]).length > 0
      ? 'รายการนี้เซ็นแล้ว — จะลบทั้งใบยินยอมและไฟล์ PDF ที่เซ็น (ใน Wasabi) อย่างถาวร · กู้คืนไม่ได้'
      : 'รายการจะถูกลบออกจาก chart · ถ้าคนไข้ได้ลิงก์นี้แล้วจะใช้ไม่ได้'
  }

  async function deleteConsent(idx) {
    const target = consents[idx]
    if (!target || !chartId) return
    setSaving(true); setErr(null)
    try {
      const matched     = matchedDocsFor(target)
      const removeDocSet = new Set(matched.map(({ i }) => i))
      const nextConsent = consents.filter((_, i) => i !== idx)
      const nextDocs    = documents.filter((_, i) => !removeDocSet.has(i))

      // 1) Patch chart first (consent + document arrays). If the Wasabi delete
      //    fails after this, the row is already gone from the UI — a later
      //    orphan sweep can reclaim the object.
      const body = { consent: nextConsent }
      if (removeDocSet.size) body.document = nextDocs
      const r = await fetch(`/api/charts/${chartId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      if (r.status === 401) { redirectToLogin(); return }
      if (!r.ok) {
        const t = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${t || r.statusText}`)
      }

      // 2) Best-effort delete of the Wasabi objects behind the signed PDFs.
      for (const { d } of matched) {
        const key = d.location || d.key
        if (!key) continue
        try {
          await fetch(`/api/files/${key.split('/').map(encodeURIComponent).join('/')}`, {
            method: 'DELETE',
            credentials: 'include',
          })
        } catch { /* non-fatal — record already removed from the chart */ }
      }
      toast.success('ลบใบยินยอมแล้ว')
      onSaved?.()
    } catch (e) {
      const msg = e?.message || 'ลบไม่สำเร็จ'
      setErr(msg)
      toast.error('ลบไม่สำเร็จ', { detail: msg })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="bg-white border border-slate-200 rounded-2xl">
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">
          <ClipboardSignature size={14} strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Consent</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">ใบยินยอม · คนไข้เซ็นใน Patient Portal</div>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-slate-400 mr-1" />}
        {canEdit && chartId && (
          <Button variant="soft" size="sm" icon={Plus} onClick={() => setShowAdd(true)}>สร้างใบยินยอม</Button>
        )}
      </header>

      <div className="p-4 sm:p-5 space-y-3">
        {err && <ErrorState title={err} />}

        {consents.length === 0 && (
          <EmptyState
            icon={ClipboardSignature}
            title="ยังไม่มี consent"
            hint={canEdit ? 'กดปุ่ม "สร้างใบยินยอม" เพื่อเพิ่มรายการ' : undefined}
          />
        )}

        {consents.length > 0 && (
          <div className="rounded-xl bg-slate-50/50 divide-y divide-slate-100">
            {consents.map((c, i) => (
              <ConsentRow
                key={c.unique_id || i}
                consent={c}
                chartId={chartId}
                patientId={patientId ?? chart?.patient_id}
                canEdit={canEdit}
                isAdmin={isAdmin}
                busy={saving}
                onUpdateNote={(v) => updateNote(i, v)}
                onRenew={() => renewConsent(i)}
                onDelete={() => setConfirmDelete(i)}
              />
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddConsentDialog
          onCancel={() => setShowAdd(false)}
          onSubmit={(payload) => { addConsent(payload); setShowAdd(false) }}
        />
      )}

      {(confirmDelete != null || deletingText) && (
        <ConfirmDialog
          title="ลบใบยินยอมนี้?"
          message={deletingText || deleteConsentText(confirmDelete)}
          confirmLabel="ลบ"
          tone="danger"
          busy={!!deletingText}
          onConfirm={async () => {
            if (deletingText) return
            const idx = confirmDelete
            setDeletingText(deleteConsentText(idx))
            try { await deleteConsent(idx) } finally { setDeletingText(null); setConfirmDelete(null) }
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Add dialog                                                                  */
/* -------------------------------------------------------------------------- */

function AddConsentDialog({ onCancel, onSubmit }) {
  const [type, setType] = useState(CONSENT_TYPES[0].value)
  const [customLabel, setCustomLabel] = useState('')
  const [note, setNote] = useState('')

  const isCustom = type === CUSTOM_TYPE
  const canSubmit = !isCustom || customLabel.trim().length > 0
  const dirty = type !== CONSENT_TYPES[0].value || customLabel.trim() !== '' || note.trim() !== ''

  function handleSubmit() {
    if (!canSubmit) return
    onSubmit({
      type,
      customLabel: isCustom ? customLabel.trim() : '',
      note: note.trim(),
    })
  }

  return (
    <Modal
      onClose={onCancel}
      title="สร้างใบยินยอม"
      subtitle="ใบยินยอมจะมีอายุ 1 ชั่วโมงนับจากตอนสร้าง"
      dirty={dirty}
      footer={(
        <DrawerFooter
          onCancel={onCancel}
          onSave={handleSubmit}
          saveLabel="สร้าง"
          canSave={canSubmit}
          status={canSubmit ? null : 'ระบุชื่อใบยินยอมก่อนสร้าง'}
        />
      )}
    >
      <div className="space-y-4">
        <Field label="เลือกประเภท" required>
          <ChoiceChips
            options={CONSENT_TYPES.filter((t) => !t.hidden).map(({ value, tone }) => ({ value, label: value, color: TONE_HEX[tone] }))}
            value={type}
            onChange={setType}
          />
        </Field>

        {isCustom && (
          <Field label="ชื่อใบยินยอม (กำหนดเอง)" required>
            <Input
              type="text"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="เช่น ยินยอมรักษาที่ไม่อยู่ในรายการ"
              autoFocus
            />
          </Field>
        )}

        <Field label="หมายเหตุ" hint="เลือกใส่">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="รายละเอียดเพิ่มเติม"
            rows={3}
          />
        </Field>
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Row                                                                         */
/* -------------------------------------------------------------------------- */

function ConsentRow({ consent, chartId, patientId, canEdit, isAdmin, busy, onUpdateNote, onRenew, onDelete }) {
  const tone   = TONE[(CONSENT_TYPES.find((t) => t.value === consent.type)?.tone) || 'slate']
  const label  = consent.type === CUSTOM_TYPE && consent.custom_label
    ? consent.custom_label
    : (consent.type || '—')
  const locked = !!consent.accept_at // note becomes read-only after acceptance
  const expired = !consent.accept_at && consent.expired_date && Date.now() > consent.expired_date

  const [noteLocal, setNoteLocal] = useState(consent.note || '')
  useEffect(() => { setNoteLocal(consent.note || '') }, [consent.note])
  function commitNote() {
    const v = noteLocal
    if (v !== (consent.note || '')) onUpdateNote?.(v)
  }

  return (
    <div className="px-3.5 py-3 hover:bg-white transition-colors first:rounded-t-xl last:rounded-b-xl">
      <div className="flex items-start gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[13px] font-semibold ring-1 ${tone.bg} ${tone.text} ${tone.ring}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
          {label}
        </span>
        {consent.type === CUSTOM_TYPE && (
          <span className="text-[11.5px] uppercase tracking-[0.12em] text-slate-400 font-semibold mt-0.5">custom</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <StatusPill consent={consent} />
          {consent.unique_id && chartId && !expired && !locked && (
            <Button
              variant="soft"
              size="sm"
              icon={ExternalLink}
              onClick={() => window.open(`/consent/${chartId}/${consent.unique_id}?p=${encodeURIComponent(patientId ?? '')}`, '_blank', 'noopener')}
              title="เปิดฟอร์มให้คนไข้อ่านและยอมรับ"
            >
              เปิดฟอร์ม
            </Button>
          )}
          {canEdit && expired && (
            <Button variant="soft" size="sm" icon={RotateCw} onClick={onRenew} disabled={busy} title="ต่ออายุใบยินยอมอีก 1 ชั่วโมง">
              ต่ออายุ +1 ชม.
            </Button>
          )}
          <ConsentMetaPopup consent={consent} />
          {/* Editors can delete unlocked consents. Admins can delete ANY consent
              (incl. accepted/locked) — that also removes the signed Wasabi PDF. */}
          {((canEdit && !locked) || isAdmin) && (
            <IconButton icon={Trash2} size="sm" variant="danger-soft" onClick={onDelete} disabled={busy}
              label={locked ? 'ลบ (รวมไฟล์ที่เซ็นแล้ว)' : 'ลบ'} />
          )}
        </div>
      </div>

      {canEdit && !locked ? (
        <textarea
          value={noteLocal}
          onChange={(e) => setNoteLocal(e.target.value)}
          onBlur={commitNote}
          placeholder="หมายเหตุ (เลือกใส่)"
          rows={2}
          className="w-full mt-2 px-2 py-1 text-[14px] border border-transparent rounded-md hover:border-slate-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15 outline-none bg-transparent focus:bg-white placeholder:text-slate-300 placeholder:italic resize-y min-h-[36px]"
        />
      ) : locked ? (
        <div className="mt-2">
          {consent.note
            ? <div className="text-[14px] text-slate-600 whitespace-pre-line">{consent.note}</div>
            : <div className="text-[13.5px] text-slate-300 italic">ไม่มีหมายเหตุ</div>}
          <div className="text-[12px] text-slate-400 mt-1 inline-flex items-center gap-1">
            <Lock size={10} strokeWidth={2.25} />
            <span>ล็อก — แก้ไขไม่ได้หลังคนไข้ยอมรับ</span>
          </div>
        </div>
      ) : (
        consent.note && <div className="mt-2 text-[14px] text-slate-600 whitespace-pre-line">{consent.note}</div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Info popup — same pattern as MetaFooter on appointment/treatment cards     */
/* -------------------------------------------------------------------------- */

function ConsentMetaPopup({ consent }) {
  const [open, setOpen] = useState(false)
  if (!consent) return null
  return (
    <div className="pt-0.5 flex justify-end">
      <div
        className="relative"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="meta info"
          className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-all ${
            open
              ? 'text-brand-700 bg-brand-50 ring-1 ring-brand-200'
              : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 ring-1 ring-transparent'
          }`}
        >
          <Info size={13} strokeWidth={2} />
        </button>
        {open && (
          <div
            role="tooltip"
            className="absolute right-0 bottom-full mb-2 z-20 min-w-[168px] rounded-lg bg-white ring-1 ring-brand-200 shadow-lg shadow-brand-900/10 overflow-hidden pointer-events-none animate-ss-pop-in"
          >
            <MetaRow label="Created">
              <span className="font-mono">{fmtDateTime(consent.created_at)}</span>
            </MetaRow>
            <MetaRow label="Modified">
              <span className="font-mono">{fmtDateTime(consent.modified_at)}</span>
            </MetaRow>
            <MetaRow label="Accepted" last>
              {consent.accept_at
                ? <span className="font-mono text-emerald-700">{fmtDateTime(consent.accept_at)}</span>
                : <span className="italic text-slate-400">ยังไม่ยอมรับ</span>}
            </MetaRow>
          </div>
        )}
      </div>
    </div>
  )
}

function MetaRow({ label, children, last }) {
  return (
    <div className={`px-3 py-1.5 ${last ? '' : 'border-b border-brand-100'}`}>
      <div className="text-[11.5px] uppercase tracking-[0.1em] font-semibold text-brand-600">{label}</div>
      <div className="text-[12.5px] text-slate-700 leading-snug">{children}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Status pill                                                                 */
/* -------------------------------------------------------------------------- */

function StatusPill({ consent }) {
  const now = Date.now()
  if (consent.accept_at) {
    return <Badge tone="emerald"><Check size={10} strokeWidth={2.75} />ยอมรับแล้ว</Badge>
  }
  const expired = consent.expired_date && now > consent.expired_date
  if (expired) {
    return <Badge tone="rose"><AlertCircle size={10} strokeWidth={2.25} />หมดอายุ</Badge>
  }
  // Pending — show remaining time.
  const remainMs = (consent.expired_date || 0) - now
  const mins = Math.max(1, Math.round(remainMs / 60000))
  const remainLabel = mins >= 60 ? `${Math.floor(mins / 60)} ชม. ${mins % 60} นาที` : `${mins} นาที`
  return <Badge tone="amber"><Clock size={10} strokeWidth={2.25} />รออีก {remainLabel}</Badge>
}
